import {
   addCommentToZendeskTicket,
   createZendeskTicketSchema,
   SupportTopic,
   SupportTopicMetadata,
   TicketCommentDto,
   TicketDto,
   welkinWebhookRequestDtoSchema,
} from '@innerwell/dtos';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const support = c.router(
   {
      zendeskWebhook: {
         method: 'POST',
         path: '/support/zendesk-webhook',
         body: z.unknown(),
         responses: {
            200: c.type<unknown>(),
         },
      },
      welkinWebhook: {
         method: 'POST',
         path: '/support/welkin-webhook',
         body: welkinWebhookRequestDtoSchema,
         responses: {
            200: c.type<unknown>(),
         },
      },

      getAvailableTopicsAndActions: {
         method: 'GET',
         path: '/support/available-topics',
         responses: {
            200: c.type<{ [Key in SupportTopic]?: SupportTopicMetadata }>(),
         },
      },

      createTicket: {
         method: 'POST',
         path: '/support/tickets',
         contentType: 'multipart/form-data',
         body: createZendeskTicketSchema,
         responses: {
            200: c.type<
               TicketDto & {
                  comments: TicketCommentDto[];
               }
            >(),
         },
      },
      getTicketById: {
         method: 'GET',
         path: '/support/tickets/:ticketId',
         pathParams: z.object({
            ticketId: z.coerce.number().or(z.string().uuid()),
         }),
         responses: {
            200: c.type<Omit<TicketDto, 'unreadComments'>>(),
         },
      },
      listTickets: {
         method: 'GET',
         path: '/support/tickets',
         query: z.object({
            nextPage: z.string().nullish(),
         }),
         responses: {
            200: c.type<{
               tickets: TicketDto[];
               nextPage: string | null;
            }>(),
         },
      },
      addCommentToTicket: {
         method: 'POST',
         path: '/support/tickets/:ticketId/comments',
         pathParams: z.object({
            ticketId: z.coerce.number().or(z.string().uuid()),
         }),
         body: addCommentToZendeskTicket,
         responses: {
            200: c.type<
               TicketDto & {
                  comments: TicketCommentDto[];
               }
            >(),
         },
      },
      getCommentsForTicket: {
         method: 'GET',
         path: '/support/tickets/:ticketId/comments',
         pathParams: z.object({
            ticketId: z.coerce.number().or(z.string().uuid()),
         }),
         responses: {
            200: c.type<TicketCommentDto[]>(),
         },
      },

      setReadTimestampForTicket: {
         method: 'POST',
         path: '/support/tickets/:ticketId/read',
         pathParams: z.object({
            ticketId: z.coerce.number().or(z.string().uuid()),
         }),
         body: z.object({
            ts: z.string().datetime().nullish(),
         }),
         responses: {
            200: c.type<null>(),
         },
      },

      uploadFileToTicket: {
         method: 'POST',
         path: '/support/tickets/:ticketId/upload',
         pathParams: z.object({
            ticketId: z.coerce.number().or(z.string().uuid()),
         }),
         contentType: 'multipart/form-data',
         body: z.object({
            // this will be picked up by multer, can't validate here
            files: z.custom<File[]>(),
            message: z.string().min(5),
         }),
         responses: {
            200: c.type<unknown>(),
         },
      },

      getFileFromTicket: {
         method: 'GET',
         path: '/support/tickets/:ticketId/download/:welkinDocId/:fileId',
         pathParams: z.object({
            ticketId: z.string().uuid(), // only welkin tickets
            welkinDocId: z.string().uuid(),
            fileId: z.string().uuid(),
         }),
         responses: {
            200: c.type<unknown>(),
         },
      },
   },
   {
      strictStatusCodes: true,
   },
);
